import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonAlert,
  IonCol,
  IonRow,
} from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import { v4 as uuidv4 } from "uuid";
import PubSub from "pubsub-js";
import { Preferences } from "@capacitor/preferences";
import { send } from "../utils/sockets/asyncSocket";
import config from "../components/helpers/socket/config";
import { pubsubEvents } from "../components/helpers/socket/pubsubEvent";
import { hapticsImpactMedium } from "../utils/core-utils/hapticUtils";
import PriceAlertModal from "../platforms/cmed/MarketData/PriceAlertModal";
import { useAlertingStatus } from "../components/contexts/AlertingStatusContext";
import { Alert } from "./notifications";
import AlertsTable from "./AlertsTable";
import { FractionalToDecimalConverter } from "../platforms/cmed/common/utils/FractionalToDecimalConverterAndValidator";
import { DecimalToFractionalConverter } from "../platforms/cmed/common/utils/DecimalToFractionalConverterAndValidator";
import {
  startMetric,
  endMetric,
  remapMetric,
} from "../utils/metric-utils/metricUtils";
import { MetricConstants, MetricTypeConstants } from "../components/Constants";
import {
  getFieldLabel,
  isPriceField,
  extractValueFromCondition,
} from "../utils/alertUtils";

interface NotificationsModalProps {
  isNotificationsModalOpen: boolean;
  setIsNotificationsModalOpen: (isOpen: boolean) => void;
}

const NotificationsModal: React.FC<NotificationsModalProps> = ({
  isNotificationsModalOpen,
  setIsNotificationsModalOpen,
}) => {
  const { alerts: realTimeStatuses } = useAlertingStatus();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const slidingItemRefs = useRef<{
    [key: string]: HTMLIonItemSlidingElement | null;
  }>({});
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [alertToCopy, setAlertToCopy] = useState<Alert | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<{
    show: boolean;
    alert: Alert | null;
  }>({ show: false, alert: null });
  const [deleteAlertConfirmationOpen, setDeleteAlertConfirmationOpen] =
    useState(false);
  const [alertToBeDeleted, setAlertToBeDeleted] = useState<Alert | null>(null);
  const [fractionalPriceType, setFractionalPriceType] = useState<
    string | undefined
  >(undefined);

  const mergedAlerts = useMemo(() => {
    return alerts.map((alert) => {
      const rtStatus = realTimeStatuses[alert.reqId];
      if (rtStatus) {
        // this is to prefer the backend's actual status if the alert has entered a terminal or explicit state like TRIGGERED, EXPIRED, PAUSED, or DELETED
        const overrideStatus = [
          "TRIGGERED",
          "EXPIRED",
          "DELETED",
          "PAUSED",
        ].includes(alert.status)
          ? alert.status
          : rtStatus.status;

        let overrideMessage = rtStatus.message;
        if (
          rtStatus.status === "ACTIVE" &&
          sessionStorage.getItem(`alert_edit_${alert.reqId}`) === "true"
        ) {
          overrideMessage = "Alert updated successfully!";
        } else if (
          rtStatus.status === "ACTIVE" &&
          sessionStorage.getItem(`alert_resume_${alert.reqId}`) === "true"
        ) {
          overrideMessage = "Alert resumed successfully!";
        }

        return {
          ...alert,
          status: overrideStatus,
          message: overrideMessage,
          marketdataSubscriptionStatus: rtStatus.marketDataSubscribed
            ? "ACTIVE"
            : rtStatus.status === "FAILED" && !rtStatus.marketDataSubscribed
              ? "FAILED"
              : "PENDING",
          queryDeploymentStatus: rtStatus.queryDeployed
            ? "ACTIVE"
            : rtStatus.status === "FAILED" && !rtStatus.queryDeployed
              ? "FAILED"
              : "PENDING",
        };
      }
      return alert;
    });
  }, [alerts, realTimeStatuses]);

  const getPaddedFractionalValue = (val: number, pType: string) => {
    let converted = DecimalToFractionalConverter(val, pType);

    if (pType === "EighthThirtySeconds") {
      const { valid, decimal_number } = FractionalToDecimalConverter(
        converted,
        pType,
      );
      if (!valid || Math.abs(decimal_number - val) > 0.00001) {
        const integer = Math.trunc(val);
        const remainder = Math.abs(val - integer);
        const thirtySeconds = Math.floor(remainder * 32 + 0.00001);
        const subRemainder = remainder * 32 - thirtySeconds;
        let eighths = Math.round(subRemainder * 8);

        if (eighths >= 4 && eighths <= 7) {
          eighths += 1;
        }

        const sign = val < 0 ? "-" : "";
        const absInt = Math.abs(integer);
        const nn = thirtySeconds.toString().padStart(2, "0");
        const s = eighths.toString();
        const candidate = `${sign}${absInt}'${nn}${s}`;

        const { valid: candidateValid, decimal_number: candidateDecimal } =
          FractionalToDecimalConverter(candidate, pType);
        if (candidateValid && Math.abs(candidateDecimal - val) < 0.00001) {
          converted = candidate;
        }
      }
    }

    if (pType !== "NotFractional") {
      const parts = converted.split("'");
      if (parts.length === 2) {
        if (
          [
            "HalfThirtySeconds",
            "QuarterThirtySeconds",
            "EighthThirtySeconds",
          ].includes(pType)
        ) {
          // Try padding to 3 digits
          const padded3 = parts[0] + "'" + parts[1].padStart(3, "0");
          let { decimal_number: dec3, valid: valid3 } =
            FractionalToDecimalConverter(padded3, pType);

          if (pType === "EighthThirtySeconds") {
            const p = padded3.split("'");
            if (p.length === 2 && p[1].length === 3) {
              const integer = parseInt(p[0], 10);
              const num = parseInt(p[1].substring(0, 2), 10);
              const sub = parseInt(p[1].substring(2), 10);
              if (!isNaN(integer) && !isNaN(num) && !isNaN(sub)) {
                const sign = padded3.startsWith("-") ? -1 : 1;
                let subVal = sub;
                if (sub === 5) subVal = 4;
                else if (sub === 7) subVal = 6;
                dec3 = sign * (Math.abs(integer) + num / 32 + subVal / 256);
                valid3 = true;
              }
            }
          }
          if (
            valid3 &&
            (Math.abs(dec3 - val) <= 0.0001 ||
              (Math.abs(val) < 0.03 &&
                parts[1].length === 2 &&
                parts[1].startsWith("0")))
          ) {
            return padded3;
          }
        }

        // Try padding to 2 digits
        const padded2 = parts[0] + "'" + parts[1].padStart(2, "0");
        const { decimal_number: dec2, valid: valid2 } =
          FractionalToDecimalConverter(padded2, pType);
        if (valid2 && Math.abs(dec2 - val) <= 0.0001) {
          return padded2;
        }
      }
    }
    const { valid } = FractionalToDecimalConverter(converted, pType);
    if (!valid && pType !== "NotFractional") {
      let tickSize = 0;
      switch (pType) {
        case "Halves":
          tickSize = 1 / 2;
          break;
        case "Eighths":
          tickSize = 1 / 8;
          if (
            isDetailsModalOpen.alert?.productCode === "ZC" ||
            isDetailsModalOpen.alert?.productCode === "ZW" ||
            isDetailsModalOpen.alert?.productCode === "ZS"
          ) {
            // These products trade in 1/4 cent increments (0.25) but are quoted in eighths.
            tickSize = 0.25;
          }
          break;
        case "ThirtySeconds":
          tickSize = 1 / 32;
          break;
        case "SixtyFourths":
          tickSize = 1 / 64;
          break;
        case "HalfThirtySeconds":
          tickSize = 1 / 64;
          break;
        case "QuarterThirtySeconds":
          tickSize = 1 / 128;
          break;
        case "EighthThirtySeconds":
          tickSize = 1 / 256;
          break;
        case "HalfSixtyFourths":
          tickSize = 1 / 128;
          break;
        default:
          tickSize = 0;
          break;
      }

      if (tickSize > 0) {
        let loopCount = 0;
        const baseVal = Math.round(val / tickSize) * tickSize;
        const checkVal = DecimalToFractionalConverter(baseVal, pType);
        if (FractionalToDecimalConverter(checkVal, pType).valid) {
          return checkVal;
        }

        while (loopCount < 50) {
          const nextValUp = baseVal + (loopCount + 1) * tickSize;
          const nextConvertedUp = DecimalToFractionalConverter(
            nextValUp,
            pType,
          );
          if (FractionalToDecimalConverter(nextConvertedUp, pType).valid) {
            return nextConvertedUp;
          }

          const nextValDown = baseVal - (loopCount + 1) * tickSize;
          const nextConvertedDown = DecimalToFractionalConverter(
            nextValDown,
            pType,
          );
          if (FractionalToDecimalConverter(nextConvertedDown, pType).valid) {
            return nextConvertedDown;
          }
          loopCount++;
        }
      }
    }
    return converted;
  };

  const formatCondition = (
    condition: string,
    type: string,
    fType: string | undefined,
    alert?: Alert,
  ) => {
    const formatWithCommas = (str: string, isFractional: boolean = false) => {
      if (!str) {
        return str;
      }
      const delimiter = isFractional && str.includes("'") ? "'" : ".";
      const parts = str.split(delimiter);
      const num = Number(parts[0]);
      if (isNaN(num)) {
        return str;
      }
      const formattedInt = num.toLocaleString("en-US");
      if (parts.length > 1) {
        return formattedInt + delimiter + parts[1];
      }
      return formattedInt;
    };

    if (alert?.criteriaList && alert.criteriaList.length > 0) {
      return alert.criteriaList
        .map((c) => {
          const op =
            c.operator === "GTE"
              ? "≥"
              : c.operator === "LTE"
                ? "≤"
                : c.operator === "GT"
                  ? ">"
                  : c.operator === "LT"
                    ? "<"
                    : "=";
          const fieldLabel = getFieldLabel(c.field);
          const val =
            isPriceField(c.field) && fType && fType !== "NotFractional"
              ? formatWithCommas(
                  getPaddedFractionalValue(c.limitValue, fType),
                  true,
                )
              : formatWithCommas(c.limitValue.toString());
          const logic = c.logicOperator ? ` ${c.logicOperator} ` : "";
          return `${fieldLabel} ${op} ${val}${logic}`;
        })
        .join("");
    }

    if (!condition) return condition;

    const val = extractValueFromCondition(condition);
    const match = condition.match(/^(.*?[<>=]+|.*?&[a-z]+;)\s*([0-9.-]+)$/);
    const opMatch = condition.match(/(&[a-z]+;|[<>=]+)/);
    const opSymbol = opMatch
      ? opMatch[1].replace(/&gt;/g, ">").replace(/&lt;/g, "<")
      : "";

    if (!isPriceField(type)) {
      if (match && val !== undefined && !isNaN(val)) {
        return `${getFieldLabel(type)} ${opSymbol} ${formatWithCommas(match[2])}`;
      }
      return condition?.replace(/&gt;/g, ">")?.replace(/&lt;/g, "<");
    }

    if (!fType) return "Loading...";

    if (fType === "NotFractional") {
      if (match && val !== undefined && !isNaN(val)) {
        return `${getFieldLabel(type)} ${opSymbol} ${formatWithCommas(match[2])}`;
      }
      return condition?.replace(/&gt;/g, ">")?.replace(/&lt;/g, "<");
    }

    if (val !== undefined && !isNaN(val)) {
      if (match) {
        const frac = getPaddedFractionalValue(val, fType);
        return `${getFieldLabel(type)} ${opSymbol} ${formatWithCommas(frac, true)}`;
      }
    }
    return condition?.replace(/&gt;/g, ">")?.replace(/&lt;/g, "<");
  };

  const fetchAlerts = useCallback(async () => {
    setIsLoading(true);
    setAlertToBeDeleted(null);
    const { value } = await Preferences.get({ key: "accessToken" });
    if (value) {
      send(config.socket.channels.CORE, {
        action: "getRequestedAlerts",
        type: "getRequestedAlerts",
        requestID: uuidv4(),
        payload: { accessToken: value },
      });
    } else {
      console.error("Access token not found");
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    Object.entries(realTimeStatuses).forEach(([reqId, rtStatus]) => {
      if (rtStatus.status === "ACTIVE" || rtStatus.status === "FAILED") {
        const isSuccess = rtStatus.status === "ACTIVE";

        endMetric(
          isSuccess
            ? MetricConstants.SET_ALERT_SUCCESS
            : MetricConstants.SET_ALERT_FAILURE,
          { reqId },
        );
        endMetric(
          isSuccess
            ? MetricConstants.EDIT_ALERT_SUCCESS
            : MetricConstants.EDIT_ALERT_FAILURE,
          { reqId },
        );
        endMetric(
          isSuccess
            ? MetricConstants.RESUME_ALERT_SUCCESS
            : MetricConstants.RESUME_ALERT_FAILURE,
          { reqId },
        );

        if (
          isSuccess &&
          sessionStorage.getItem(`alert_edit_toast_${reqId}`) === "true"
        ) {
          PubSub.publish(
            pubsubEvents.successKeys.TRADING_SERVICE_SUCCESS_TOAST,
            "Alert updated successfully!",
          );
          sessionStorage.removeItem(`alert_edit_toast_${reqId}`);
        }
        if (
          isSuccess &&
          sessionStorage.getItem(`alert_resume_toast_${reqId}`) === "true"
        ) {
          PubSub.publish(
            pubsubEvents.successKeys.TRADING_SERVICE_SUCCESS_TOAST,
            "Alert resumed successfully!",
          );
          sessionStorage.removeItem(`alert_resume_toast_${reqId}`);
        }
      }
    });
  }, [realTimeStatuses]);

  useEffect(() => {
    const alertNotificationToken = PubSub.subscribe(
      pubsubEvents.successKeys.ALERT_NOTIFICATION_TOAST,
      () => {
        setTimeout(() => {
          fetchAlerts();
        }, 500);
      },
    );

    const requestedAlertsToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_REQUESTED_ALERTS_UPDATE,
      (msg, data) => {
        if (data.status === "SUCCESS" && data.payload) {
          if (Array.isArray(data.payload)) {
            const sortedAlerts = data.payload.sort((a: Alert, b: Alert) => {
              const aTime =
                Number(a.modifiedAt) < 1e11
                  ? Number(a.modifiedAt) * 1000
                  : Number(a.modifiedAt);
              const bTime =
                Number(b.modifiedAt) < 1e11
                  ? Number(b.modifiedAt) * 1000
                  : Number(b.modifiedAt);
              return bTime - aTime;
            });
            setAlerts(sortedAlerts);
          } else {
            setAlerts([]);
          }
        } else {
          setAlerts([]);
          console.error(
            "Failed to get requested alerts or payload is invalid:",
            data?.errors,
          );
        }
        setIsLoading(false);
      },
    );

    const startSuccessToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_ALERT_START_SUCCESS,
      (msg, data) => {
        if (data.payload && data.payload.reqId) {
          remapMetric(
            MetricConstants.SET_ALERT,
            data.requestID,
            data.payload.reqId,
          );
        }
        fetchAlerts();
      },
    );

    const editSuccessToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_ALERT_EDIT_SUCCESS,
      (msg, data) => {
        if (data.payload && data.payload.reqId) {
          sessionStorage.setItem(`alert_edit_${data.payload.reqId}`, "true");
          sessionStorage.removeItem(`alert_resume_${data.payload.reqId}`);

          if (data.requestID === data.payload.reqId) {
            //in case of editing paused alert, both reqIds are same
            endMetric(MetricConstants.EDIT_ALERT_SUCCESS, {
              reqId: data.payload.reqId,
            });
            PubSub.publish(
              pubsubEvents.successKeys.TRADING_SERVICE_SUCCESS_TOAST,
              "Alert updated successfully!",
            );
          } else {
            sessionStorage.setItem(
              `alert_edit_toast_${data.payload.reqId}`,
              "true",
            );
            remapMetric(
              MetricConstants.EDIT_ALERT,
              data.requestID,
              data.payload.reqId,
            );
          }
        }
        fetchAlerts();
      },
    );

    const deleteSuccessToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_ALERT_DELETE_SUCCESS,
      (msg, data) => {
        endMetric(MetricConstants.DELETE_ALERT_SUCCESS, {
          reqId: data.requestID,
        });
        fetchAlerts();
      },
    );

    const pauseSuccessToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_ALERT_PAUSE_SUCCESS,
      (msg, data) => {
        endMetric(MetricConstants.PAUSE_ALERT_SUCCESS, {
          reqId: data.requestID,
        });
        setTimeout(() => {
          fetchAlerts();
        }, 1000);
      },
    );

    const resumeSuccessToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_ALERT_RESUME_SUCCESS,
      (msg, data) => {
        if (data.payload && data.payload.reqId) {
          sessionStorage.setItem(`alert_resume_${data.payload.reqId}`, "true");
          sessionStorage.setItem(
            `alert_resume_toast_${data.payload.reqId}`,
            "true",
          );
          sessionStorage.removeItem(`alert_edit_${data.payload.reqId}`);
          remapMetric(
            MetricConstants.RESUME_ALERT,
            data.requestID,
            data.payload.reqId,
          );
        }
        fetchAlerts();
      },
    );

    const deleteFailureToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_ALERT_DELETE_FAILURE,
      (msg, data) => {
        endMetric(MetricConstants.DELETE_ALERT_FAILURE, {
          reqId: data.requestID,
        });
      },
    );

    const pauseFailureToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_ALERT_PAUSE_FAILURE,
      (msg, data) => {
        endMetric(MetricConstants.PAUSE_ALERT_FAILURE, {
          reqId: data.requestID,
        });
      },
    );

    const resumeFailureToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_ALERT_RESUME_FAILURE,
      (msg, data) => {
        endMetric(MetricConstants.RESUME_ALERT_FAILURE, {
          reqId: data.requestID,
        });
      },
    );

    const fractionalTypeSuccessToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_GET_FRACTIONAL_TYPE_SUCCESS,
      (msg, data) => {
        setFractionalPriceType(
          data?.payload?.FractionalPriceType || "NotFractional",
        );
      },
    );

    const fractionalTypeFailureToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_GET_FRACTIONAL_TYPE_FAILURE,
      (msg, data) => {
        setFractionalPriceType("NotFractional");
      },
    );

    return () => {
      PubSub.unsubscribe(alertNotificationToken);
      PubSub.unsubscribe(requestedAlertsToken);
      PubSub.unsubscribe(startSuccessToken);
      PubSub.unsubscribe(editSuccessToken);
      PubSub.unsubscribe(deleteSuccessToken);
      PubSub.unsubscribe(pauseSuccessToken);
      PubSub.unsubscribe(resumeSuccessToken);
      PubSub.unsubscribe(deleteFailureToken);
      PubSub.unsubscribe(pauseFailureToken);
      PubSub.unsubscribe(resumeFailureToken);
      PubSub.unsubscribe(fractionalTypeSuccessToken);
      PubSub.unsubscribe(fractionalTypeFailureToken);
    };
  }, [fetchAlerts]);

  const handleEditAlert = (alert: Alert) => {
    // if (alert.status === "PAUSED") {
    //   const reqId = uuidv4();
    //   startMetric(MetricConstants.EDIT_ALERT, MetricTypeConstants.TIMER, {
    //     reqId,
    //   });
    // }
    setSelectedAlert(alert);
    setIsEditModalOpen(true);
    slidingItemRefs.current[alert.reqId]?.close();
  };

  const handleCopyAlert = (alert: Alert) => {
    setAlertToCopy(alert);
    setIsCopyModalOpen(true);
    slidingItemRefs.current[alert.reqId]?.close();
  };

  const handleViewDetails = async (alert: Alert) => {
    setFractionalPriceType(undefined);
    setIsDetailsModalOpen({ show: true, alert });
    const { value: accessToken } = await Preferences.get({
      key: "accessToken",
    });
    if (accessToken && alert.securityId) {
      send(config.socket.channels.CORE, {
        action: "getFractionalType",
        type: "getFractionalType",
        requestID: uuidv4(),
        payload: {
          accessToken,
          productISIN: alert.securityId,
        },
      });
    }
  };

  const handleDeleteAlert = async (alert: Alert) => {
    hapticsImpactMedium();
    const { value } = await Preferences.get({ key: "accessToken" });
    if (value) {
      const reqId = uuidv4();
      startMetric(MetricConstants.DELETE_ALERT, MetricTypeConstants.TIMER, {
        reqId,
      });
      send(config.socket.channels.CORE, {
        action: "alertDeleteRequest",
        type: "alertDeleteRequest",
        requestID: reqId,
        payload: {
          accessToken: value,
          reqId: alert.reqId,
          productCode: alert.productCode,
        },
      });
      slidingItemRefs.current[alert.reqId]?.close();
    }
  };

  const handlePauseAlert = async (alert: Alert) => {
    hapticsImpactMedium();
    const { value } = await Preferences.get({ key: "accessToken" });
    if (value) {
      const reqId = uuidv4();
      startMetric(MetricConstants.PAUSE_ALERT, MetricTypeConstants.TIMER, {
        reqId,
      });
      send(config.socket.channels.CORE, {
        action: "alertPauseRequest",
        type: "alertPauseRequest",
        requestID: reqId,
        payload: {
          accessToken: value,
          reqId: alert.reqId,
          productCode: alert.productCode,
        },
      });
      slidingItemRefs.current[alert.reqId]?.close();
    }
  };

  const handleResumeAlert = async (alert: Alert) => {
    hapticsImpactMedium();
    const { value } = await Preferences.get({ key: "accessToken" });
    if (value) {
      const reqId = uuidv4();
      startMetric(MetricConstants.RESUME_ALERT, MetricTypeConstants.TIMER, {
        reqId,
      });
      send(config.socket.channels.CORE, {
        action: "alertResumeRequest",
        type: "alertResumeRequest",
        requestID: reqId,
        payload: {
          accessToken: value,
          reqId: alert.reqId,
          productCode: alert.productCode,
        },
      });
      slidingItemRefs.current[alert.reqId]?.close();
    }
  };

  const activeAlertDetail = useMemo(() => {
    return (
      mergedAlerts.find((a) => a.reqId === isDetailsModalOpen.alert?.reqId) ||
      null
    );
  }, [mergedAlerts, isDetailsModalOpen.alert?.reqId]);

  const handleRetryAlert = async (alert: Alert) => {
    hapticsImpactMedium();
    const { value: accessToken } = await Preferences.get({
      key: "accessToken",
    });

    if (accessToken) {
      const reqId = uuidv4();
      startMetric(MetricConstants.EDIT_ALERT, MetricTypeConstants.TIMER, {
        reqId,
      });
      const getOperatorEnum = (symbol: string) => {
        switch (symbol) {
          case ">=":
            return "GTE";
          case ">":
            return "GT";
          case "=":
            return "EQ";
          case "<=":
            return "LTE";
          case "<":
            return "LT";
          default:
            return "";
        }
      };

      const conditionParts = alert.condition ? alert.condition.split(" ") : [];
      const operatorSymbol = conditionParts.length > 1 ? conditionParts[1] : "";
      const operatorEnum = getOperatorEnum(operatorSymbol);
      const limitValue = extractValueFromCondition(alert.condition);

      send(config.socket.channels.CORE, {
        action: "alertEditRequest",
        type: "alertEditRequest",
        requestID: reqId,
        payload: {
          accessToken,
          alertName: alert.alertName,
          source: "CMEDMOBILE",
          reqId: alert.reqId,
          criteriaList:
            alert.criteriaList && alert.criteriaList.length > 0
              ? alert.criteriaList
              : [
                  {
                    securityId: parseInt(alert.securityId),
                    productCode: alert.productCode,
                    field:
                      alert.type === "VOLUME" ? "tradePriceQty" : "tradePrice",
                    operator: operatorEnum,
                    limitValue: limitValue || 0,
                  },
                ],
        },
      });

      PubSub.publish(
        pubsubEvents.successKeys.TRADING_SERVICE_SUCCESS_TOAST,
        "Retrying alert deployment...",
      );
      slidingItemRefs.current[alert.reqId]?.close();
    }
  };

  const editTradingData = React.useMemo(() => {
    if (!selectedAlert) return null;
    const customData1 = selectedAlert?.customData1;
    const symbol =
      selectedAlert.symbol ||
      (selectedAlert.criteriaList?.[0] as any)?.instrumentSymbol ||
      "";
    return {
      c: customData1 ? customData1 : selectedAlert.productCode + " " + symbol,
      g: selectedAlert.securityId,
      b: { r: 0, f: "" },
      bb: { r: 0, f: "" },
      bo: { r: 0, f: "" },
      boq: { r: 0, f: "" },
      i: false,
      id: "",
      ii: 0,
      l: "",
      lt: 0,
      n: "",
      s: 0,
      t: "",
      ts: "",
      wo: 0,
      gi: "",
    } as any;
  }, [selectedAlert]);

  const copyTradingData = React.useMemo(() => {
    if (!alertToCopy) return null;
    const customData1 = alertToCopy?.customData1;
    const symbol =
      alertToCopy.symbol ||
      (alertToCopy.criteriaList?.[0] as any)?.instrumentSymbol ||
      "";
    return {
      c: customData1 ? customData1 : alertToCopy.productCode + " " + symbol,
      g: alertToCopy.securityId,
      b: { r: 0, f: "" },
      bb: { r: 0, f: "" },
      bo: { r: 0, f: "" },
      boq: { r: 0, f: "" },
      i: false,
      id: "",
      ii: 0,
      l: "",
      lt: 0,
      n: "",
      s: 0,
      t: "",
      ts: "",
      wo: 0,
      gi: "",
    } as any;
  }, [alertToCopy]);

  return (
    <>
      <IonModal
        isOpen={isNotificationsModalOpen}
        onDidDismiss={() => {
          setIsNotificationsModalOpen(false);
        }}
        onWillPresent={fetchAlerts}
      >
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonTitle>
              <p className="ion-no-margin text-bold">MANAGE ALERTS</p>
            </IonTitle>
            <IonButtons slot="end">
              <IonButton
                onClick={() => setIsNotificationsModalOpen(false)}
                slot="icon-only"
                fill="clear"
              >
                <IonIcon color="dark" icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <AlertsTable
            alerts={mergedAlerts}
            isLoading={isLoading}
            handleViewDetails={handleViewDetails}
            handleEditAlert={handleEditAlert}
            handleCopyAlert={handleCopyAlert}
            handlePauseAlert={handlePauseAlert}
            handleResumeAlert={handleResumeAlert}
            handleRetryAlert={handleRetryAlert}
            setDeleteAlertConfirmationOpen={setDeleteAlertConfirmationOpen}
            setAlertToBeDeleted={setAlertToBeDeleted}
            slidingItemRefs={slidingItemRefs}
          />
        </IonContent>
      </IonModal>
      <IonModal
        isOpen={isDetailsModalOpen.show}
        onDidDismiss={() => setIsDetailsModalOpen({ show: false, alert: null })}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              <p className="ion-no-margin text-bold">ALERT DETAILS</p>
            </IonTitle>
            <IonButtons slot="end">
              <IonButton
                onClick={() =>
                  setIsDetailsModalOpen({ show: false, alert: null })
                }
                slot="icon-only"
                fill="clear"
              >
                <IonIcon color="dark" icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {activeAlertDetail && (
            <div style={{ padding: "13px" }}>
              {[
                {
                  label: "ALERT NAME",
                  value: activeAlertDetail?.alertName?.replaceAll("_", " "),
                },
                {
                  label: "PRODUCT",
                  value:
                    activeAlertDetail?.extName ||
                    (activeAlertDetail?.criteriaList?.[0] as any)
                      ?.instrumentExternalName,
                },
                {
                  label: "INSTRUMENT",
                  value:
                    activeAlertDetail?.customData1 ||
                    activeAlertDetail?.symbol ||
                    (activeAlertDetail?.criteriaList?.[0] as any)
                      ?.instrumentSymbol,
                },
                {
                  label: "CONDITION",
                  value: formatCondition(
                    activeAlertDetail?.condition || "",
                    activeAlertDetail?.type || "PRICE",
                    fractionalPriceType,
                    activeAlertDetail,
                  ),
                },
                {
                  label: "SOURCE",
                  value: activeAlertDetail?.source,
                },
                {
                  label: "STATUS",
                  value: activeAlertDetail?.status ?? "INACTIVE",
                },
                {
                  label: "LAST UPDATED TIME",
                  value: new Date(
                    Number(activeAlertDetail?.modifiedAt) < 1e11
                      ? Number(activeAlertDetail?.modifiedAt) * 1000
                      : Number(activeAlertDetail?.modifiedAt),
                  ).toLocaleString(),
                },
              ].map((field, index) => (
                <IonRow
                  key={index}
                  style={{ marginTop: index === 0 ? "0px" : "10px" }}
                >
                  <IonCol size="12">
                    <p
                      className="ion-no-margin text-bold"
                      style={{
                        fontSize: "10px",
                        marginBottom: "10px",
                        textTransform: "uppercase",
                      }}
                    >
                      {field.label}
                    </p>
                    <div
                      style={{
                        display: "flex",
                        minHeight: "41px",
                        alignItems: "center",
                        padding: "12px 16px",
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.10)",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          wordBreak: "break-word",
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                          width: "100%",
                          display: "block",
                        }}
                      >
                        {field.value}
                      </span>
                    </div>
                  </IonCol>
                </IonRow>
              ))}
            </div>
          )}
        </IonContent>
      </IonModal>
      {selectedAlert && (
        <PriceAlertModal
          isOpen={isEditModalOpen}
          onDidDismiss={() => {
            setIsEditModalOpen(false);
            setSelectedAlert(null);
          }}
          tradingGridGroupData={editTradingData}
          isEditMode={true}
          alertData={selectedAlert}
        />
      )}
      {alertToCopy && (
        <PriceAlertModal
          isOpen={isCopyModalOpen}
          onDidDismiss={() => {
            setIsCopyModalOpen(false);
            setAlertToCopy(null);
          }}
          tradingGridGroupData={copyTradingData}
          isEditMode={false}
          alertData={alertToCopy}
        />
      )}
      <IonAlert
        className="one-alert"
        isOpen={deleteAlertConfirmationOpen}
        header="Delete Alert"
        message={`Are you sure you want to delete?`}
        onDidDismiss={() => setDeleteAlertConfirmationOpen(false)}
        buttons={[
          {
            text: "No",
            cssClass: "alert-button-cancel",
          },
          {
            text: "Yes",
            cssClass: "alert-button-confirm",
            handler: () => {
              alertToBeDeleted && handleDeleteAlert(alertToBeDeleted);
            },
          },
        ]}
      ></IonAlert>
    </>
  );
};

export default NotificationsModal;
